// Global functions for cookie message settings
/**
 * Get cookie message settings from window object or use defaults
 * @returns {{title: string, text: string, buttonText: string}}
 */
function getCookieMessageSettings() {
  const defaults = {
    title: 'Targeted Advertising Consent Required',
    text: 'To take this quiz, please enable targeted advertising cookies. This allows us to save your quiz progress and provide personalized results.',
    buttonText: 'Update Cookie Settings'
  };

  if (window.QUIZ_COOKIE_SETTINGS) {
    return {
      title: window.QUIZ_COOKIE_SETTINGS.title || defaults.title,
      text: window.QUIZ_COOKIE_SETTINGS.text || defaults.text,
      buttonText: window.QUIZ_COOKIE_SETTINGS.buttonText || defaults.buttonText
    };
  }

  return defaults;
}

/**
 * Escape HTML to prevent XSS
 * @param {string} text
 * @returns {string}
 */
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// Check for quiz app wrapper and handle cookie consent
(function initQuizCookieCheck() {
  
  function findQuizWrapper() {
    return document.querySelector('.quiz-app-wrapper');
  }

  function waitForQuizWrapper() {
    return new Promise((resolve) => {
      const wrapper = findQuizWrapper();
      if (wrapper) {
        resolve(wrapper);
        return;
      }
      
      const observer = new MutationObserver(() => {
        const wrapper = findQuizWrapper();
        if (wrapper) {
          observer.disconnect();
          resolve(wrapper);
        }
      });
      
      observer.observe(document.body, { childList: true, subtree: true });
      
      // Timeout after 10 seconds
      setTimeout(() => {
        observer.disconnect();
        resolve(null);
      }, 10000);
    });
  }

  async function checkCookieConsent() {
    // Wait for Osano (non-blocking, times out after 2 seconds)
    await waitForOsano(2000);

    // First, check Osano's marketing consent specifically
    if (window.Osano && window.Osano.cm && window.Osano.cm.getConsent) {
      try {
        const consent = window.Osano.cm.getConsent();
        // If consent object exists, check MARKETING status
        if (consent) {
          // If MARKETING is explicitly ACCEPT, allow quiz (no message)
          if (consent.MARKETING === 'ACCEPT') {
            return false; // Don't block
          }
          // If MARKETING is explicitly DENY, block quiz (show message)
          if (consent.MARKETING === 'DENY') {
            return true; // Block quiz
          }
          // If MARKETING is not set, check if user has engaged with consent banner
          // by checking if any other consent categories are set
          const hasEngaged = consent.ANALYTICS || consent.ESSENTIAL || consent.PERSONALIZATION || 
                            consent.STORAGE || consent['OPT-OUT'];
          
          if (hasEngaged) {
            // User has engaged with consent but hasn't set MARKETING, block quiz (show message)
            return true; // Block quiz
          }
          // User hasn't engaged with consent banner at all, allow quiz (no message)
          return false; // Don't block
        }
        // If consent is null/undefined (user hasn't engaged), allow quiz (no message)
        return false; // Don't block
      } catch (e) {
        console.warn('[QUIZ RESULTS] Could not get Osano consent:', e);
      }
    }

      // Fallback to Shopify API if Osano is not available
      // Note: Shopify API doesn't distinguish between "not set" and "denied" for specific categories
      // So we'll allow quiz by default (no message) when using Shopify fallback
      return new Promise((resolve) => {
        if (window.Shopify && window.Shopify.customerPrivacy) {
          const canTrack = window.Shopify.customerPrivacy.userCanBeTracked();
          // If tracking is allowed, don't block
          // If tracking is not allowed, we can't determine if marketing was explicitly denied
          // So we'll allow quiz by default (no message)
          resolve(false); // Don't block by default
          return;
        }

        if (window.Shopify && window.Shopify.loadFeatures) {
          window.Shopify.loadFeatures(
            [{ name: 'consent-tracking-api', version: '0.1' }],
            (error) => {
              if (error) {
                // If API fails, allow quiz (no message)
                resolve(false);
                return;
              }
              setTimeout(() => {
                // Can't determine explicit marketing denial from Shopify API alone
                resolve(false); // Don't block by default
              }, 200);
            }
          );
        } else {
          setTimeout(() => {
            // If Shopify is not available, allow quiz (no message)
            resolve(false);
          }, 500);
        }
      });
  }

  async function waitForOsano(maxWait = 5000) {
    const startTime = Date.now();
    return new Promise((resolve) => {
      const checkOsano = () => {
        if (window.Osano && window.Osano.cm) {
          resolve(true);
          return;
        }
        if (Date.now() - startTime >= maxWait) {
          resolve(false);
          return;
        }
        setTimeout(checkOsano, 100);
      };
      checkOsano();
    });
  }

  /**
   * @param {HTMLElement} wrapper
   */
  function showCookieMessage(wrapper) {
    const section = wrapper.closest('section.app-section');
    if (!section) return;
    
    section.classList.add('cookie-consent-required');
    wrapper.style.display = 'none';
    
    const settings = getCookieMessageSettings();
    
    const messageHTML = `
      <div class="quiz-cookie-message">
        <div class="quiz-cookie-message__content">
          <h2 class="quiz-cookie-message__title">${escapeHtml(settings.title)}</h2>
          <div class="quiz-cookie-message__text">${settings.text}</div>
          <button type="button" class="quiz-cookie-message__button button">
            ${escapeHtml(settings.buttonText)}
          </button>
        </div>
      </div>
    `;
    
    const messageDiv = document.createElement('div');
    messageDiv.innerHTML = messageHTML;
    
    // Insert message at the beginning of the section, or append if wrapper is not a direct child
    if (section.contains(wrapper) && wrapper.parentElement === section) {
      section.insertBefore(messageDiv, wrapper);
    } else {
      // Append to section or wrapper's parent
      const container = wrapper.parentElement || section;
      container.insertBefore(messageDiv, wrapper);
    }
    
    const button = messageDiv.querySelector('.quiz-cookie-message__button');
    if (button) {
      button.addEventListener('click', async () => {
        // Check if Osano is available
        if (window.Osano && window.Osano.cm && window.Osano.cm.showDrawer) {
          try {
            window.Osano.cm.showDrawer('osano-cm-dom-info-dialog-open');
          } catch (e) {
            console.error('[QUIZ RESULTS] Failed to open Osano drawer', e);
            // Fallback: try to find privacy policy link or show instructions
            showCookieInstructionsFallback();
          }
        } else {
          // Osano not available - show instructions for Shopify native consent
          showCookieInstructionsFallback();
        }
      });
    }
    
    /**
     * Fallback when Osano drawer is not available
     * Shows instructions for Shopify native consent or links to privacy policy
     */
    function showCookieInstructionsFallback() {
      const privacyAnchor = document.querySelector('a[href*="privacy"]');
      const privacyLink = (privacyAnchor instanceof HTMLAnchorElement ? privacyAnchor.href : null) || '/policies/privacy-policy';
      const message = `To enable cookies:\n\n1. Look for the cookie banner at the bottom of the page\n2. Click "Accept" or "Allow cookies"\n3. Refresh this page\n\nOr visit our Privacy Policy: ${privacyLink}`;
      
      // Try to open privacy policy in new tab, or show alert
      if (privacyLink && privacyLink.startsWith('http')) {
        window.open(privacyLink, '_blank');
      } else {
        alert(message);
      }
    }
    
    // Listen for consent changes
    const consentListener = async function() {
      await new Promise(resolve => setTimeout(resolve, 500));
      // checkCookieConsent returns true if quiz should be blocked (show message)
      const shouldBlock = await checkCookieConsent();
      if (!shouldBlock) {
        // Marketing consent is now accepted or not set, remove message and show quiz
        section.classList.remove('cookie-consent-required');
        messageDiv.remove();
        wrapper.style.display = '';
        document.removeEventListener('visitorConsentCollected', consentListener);
      }
    };
    document.addEventListener('visitorConsentCollected', consentListener);
  }

  async function init() {
    const wrapper = await waitForQuizWrapper();
    if (!wrapper) return;
    
    // checkCookieConsent returns true if quiz should be blocked (show message)
    const shouldBlock = await checkCookieConsent();
    
    if (shouldBlock) {
      showCookieMessage(wrapper);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

if (!customElements.get('quiz-results')) {
  // Ensure CustomElement is available
  if (typeof CustomElement === 'undefined') {
    class CustomElement extends HTMLElement {
      constructor() {
        super();
      }
    }
    window.CustomElement = CustomElement;
  }

  class QuizResults extends CustomElement {
    constructor() {
      super();
      this.consentChecked = false;
      this.consentListener = null;
    }

    connectedCallback() {
      this.checkConsentAndLoad();
    }

    disconnectedCallback() {
      // Clean up listener when element is removed
      if (this.consentListener) {
        document.removeEventListener('visitorConsentCollected', this.consentListener);
        this.consentListener = null;
      }
    }

    /**
     * Check if cookies are accepted before loading the quiz
     */
    async checkConsentAndLoad() {
      const containerEl = this.closest('section.app-section');
      if (!containerEl || !(containerEl instanceof HTMLElement)) {
        throw new Error('[QUIZ RESULTS] Could not find container element');
      }

      // checkCookieConsent returns true if quiz should be blocked (show message)
      const shouldBlock = await this.checkCookieConsent();
      
      if (shouldBlock) {
        this.showCookieMessage(containerEl);
        this.setupConsentListener(containerEl);
        return;
      }

      // Marketing consent is accepted or not set, proceed with loading
      this.embedPage();
    }

    /**
     * Wait for Osano to be available
     * @returns {Promise<boolean>}
     */
    async waitForOsano(maxWait = 5000) {
      const startTime = Date.now();
      return new Promise((resolve) => {
        const checkOsano = () => {
          if (window.Osano && window.Osano.cm) {
            resolve(true);
            return;
          }

          if (Date.now() - startTime >= maxWait) {
            console.warn('[QUIZ RESULTS] Osano not available after timeout');
            resolve(false);
            return;
          }

          setTimeout(checkOsano, 100);
        };
        checkOsano();
      });
    }

    /**
     * Check if user has accepted cookies
     * Based on Osano + Shopify integration: https://api.partners.osano.com/
     * @returns {Promise<boolean>}
     */
    /**
     * Check if user has explicitly denied marketing consent (targeted advertising)
     * Only shows message if consent was explicitly DENIED, not if consent hasn't been set
     * Based on Osano + Shopify integration: https://api.partners.osano.com/
     * @returns {Promise<boolean>} Returns true if quiz should be blocked (show message), false if allowed
     */
    async checkCookieConsent() {
      // Wait for Osano (non-blocking, times out after 2 seconds)
      await this.waitForOsano(2000);

      // First, check Osano's marketing consent specifically
      if (window.Osano && window.Osano.cm && window.Osano.cm.getConsent) {
        try {
          const consent = window.Osano.cm.getConsent();
          // If consent object exists, check MARKETING status
          if (consent) {
            // If MARKETING is explicitly ACCEPT, allow quiz (no message)
            if (consent.MARKETING === 'ACCEPT') {
              return false; // Don't block
            }
            // If MARKETING is explicitly DENY, block quiz (show message)
            if (consent.MARKETING === 'DENY') {
              return true; // Block quiz
            }
            // If MARKETING is not set, check if user has engaged with consent banner
            // by checking if any other consent categories are set
            const hasEngaged = consent.ANALYTICS || consent.ESSENTIAL || consent.PERSONALIZATION || 
                              consent.STORAGE || consent['OPT-OUT'];
            
            if (hasEngaged) {
              // User has engaged with consent but hasn't set MARKETING, block quiz (show message)
              return true; // Block quiz
            }
            // User hasn't engaged with consent banner at all, allow quiz (no message)
            return false; // Don't block
          }
          // If consent is null/undefined (user hasn't engaged), allow quiz (no message)
          return false; // Don't block
        } catch (e) {
          console.warn('[QUIZ RESULTS] Could not get Osano consent:', e);
        }
      }

      // Fallback to Shopify API if Osano is not available
      // Note: Shopify API doesn't distinguish between "not set" and "denied" for specific categories
      // So we'll allow quiz by default (no message) when using Shopify fallback
      return new Promise((resolve) => {
        // If API is already available, check immediately
        if (window.Shopify && window.Shopify.customerPrivacy) {
          const canTrack = window.Shopify.customerPrivacy.userCanBeTracked();
          // If tracking is allowed, don't block
          // If tracking is not allowed, we can't determine if marketing was explicitly denied
          // So we'll allow quiz by default (no message)
          resolve(false); // Don't block by default
          return;
        }

        // If Shopify API is not ready, try loading it
        if (window.Shopify && window.Shopify.loadFeatures) {
          window.Shopify.loadFeatures(
            [
              {
                name: 'consent-tracking-api',
                version: '0.1',
              },
            ],
            (error) => {
              if (error) {
                // If API fails, allow quiz (no message)
                resolve(false);
                return;
              }

              // Wait a moment for consent API to initialize
              setTimeout(() => {
                // Can't determine explicit marketing denial from Shopify API alone
                resolve(false); // Don't block by default
              }, 200);
            }
          );
        } else {
          // If Shopify is not available, allow quiz (no message)
          setTimeout(() => {
            resolve(false); // Don't block by default
          }, 500);
        }
      });
    }

    /**
     * Show cookie consent message
     * @param {HTMLElement} containerEl
     */
    showCookieMessage(containerEl) {
      containerEl.classList.add('quiz-app-section', 'cookie-consent-required');
      
      const settings = getCookieMessageSettings();
      
      const messageHTML = `
        <div class="quiz-cookie-message">
          <div class="quiz-cookie-message__content">
            <h2 class="quiz-cookie-message__title">${escapeHtml(settings.title)}</h2>
            <div class="quiz-cookie-message__text">${settings.text}</div>
            <button 
              type="button" 
              class="quiz-cookie-message__button button"
            >
              ${escapeHtml(settings.buttonText)}
            </button>
          </div>
        </div>
      `;

      containerEl.innerHTML = messageHTML;
      
      // Add click event listener to the button
      const button = containerEl.querySelector('.quiz-cookie-message__button');
      if (button && button instanceof HTMLButtonElement) {
        button.addEventListener('click', async () => {
          // Disable button while opening drawer
          button.disabled = true;
          button.textContent = 'Opening...';
          await this.openCookieDrawer();
          // Re-enable button after a short delay
          setTimeout(() => {
            button.disabled = false;
            button.textContent = settings.buttonText;
          }, 1000);
        });
      }
    }

    /**
     * Open Osano cookie drawer
     * Uses the method documented in Osano's Shopify integration guide
     */
    async openCookieDrawer() {
      // Check if Osano is available
      const osanoReady = await this.waitForOsano(1000);
      
      if (osanoReady && window.Osano && window.Osano.cm && window.Osano.cm.showDrawer) {
        try {
          // Open the Osano cookie preferences drawer
          // This matches the method used in footer.liquid
          window.Osano.cm.showDrawer('osano-cm-dom-info-dialog-open');
          return;
        } catch (e) {
          console.error('[QUIZ RESULTS] Failed to open Osano drawer', e);
        }
      }
      
      // Fallback: Osano not available - show instructions for Shopify native consent
      // Try to find privacy policy link
      const privacyAnchor = document.querySelector('a[href*="privacy"]');
      const privacyLink = (privacyAnchor instanceof HTMLAnchorElement ? privacyAnchor.href : null) || '/policies/privacy-policy';
      const message = `To enable cookies:\n\n1. Look for the cookie banner at the bottom of the page\n2. Click "Accept" or "Allow cookies"\n3. Refresh this page\n\nOr visit our Privacy Policy: ${privacyLink}`;
      
      // Try to open privacy policy in new tab, or show alert
      if (privacyLink && privacyLink.startsWith('http')) {
        window.open(privacyLink, '_blank');
      } else {
        alert(message);
      }
    }

    /**
     * Setup listener for consent changes
     * @param {HTMLElement} containerEl
     */
    setupConsentListener(containerEl) {
      this.consentListener = async () => {
        // Wait a moment for consent to be processed
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // checkCookieConsent returns true if quiz should be blocked (show message)
        const shouldBlock = await this.checkCookieConsent();
        if (!shouldBlock) {
          // Marketing consent is now accepted or not set, remove message and load quiz
          containerEl.classList.remove('cookie-consent-required');
          this.embedPage();
        }
      };

      document.addEventListener('visitorConsentCollected', this.consentListener);
    }

    async embedPage() {
      const page = this.getAttribute('page');
      const res = await fetch(`/pages/${page}`);

      if (res.status !== 200) {
        console.error(
          '[QUIZ RESULTS] Failed to load results page %s (%s)',
          page,
          res.status,
        );
        return;
      }

      /** @type {HTMLElement|null} */
      const containerEl = this.closest('section.app-section');

      if (!containerEl) {
        throw new Error('[QUIZ REUSLTS] Could not find container element');
      }

      containerEl.classList.add('quiz-app-section', 'is-loading');

      const html = await res.text();
      const children = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('main');

      if (!children) {
        return;
      }

      const scriptTags = children.querySelectorAll('script');

      containerEl.innerHTML = children.innerHTML;
      this.injectScriptTags(scriptTags);

      containerEl.classList.add('is-loading-out');

      window.setTimeout(() => {
        containerEl.classList.remove('is-loading-out', 'is-loading');
      }, 360);

      console.log(
        '%c[QUIZ RESULTS] Injected page %s',
        'color: orange; font-weight:bold',
        page,
      );
    }

    /**
     * @param {NodeListOf<HTMLScriptElement>} tags
     */
    injectScriptTags(tags) {
      const sources = [...tags].map(tag => tag.src).filter(Boolean);
      const uniqueSources = [...new Set(sources)];
      for (const src of uniqueSources) {
        const script = document.createElement('script');
        script.src = src;
        script.defer = true;
        document.body.appendChild(script);
      }
    }
  }

  customElements.define('quiz-results', QuizResults);
}
